import { ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import { BILLING_TYPE } from "../../../invoice/InvoiceBillingType"
import { PREFERENCE_TYPE } from "../../constants"
import invoiceConfiguration, { InvoiceColumnIds } from "./invoices"

const ReturnColumnIds = InvoiceColumnIds

const columnDefinitions: ColumnDefinition[] = [
    {
        id: ReturnColumnIds.IDENTIFIER,
        sortId: "identifierInteger",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/returns/{id}/edit",
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: ReturnColumnIds.LOCATION_NAME,
        sortId: "locationNameSort",
        dataType: DataType.LOCATION,
        dataAttributes: {
            redirect: "/locations/{locationId}/edit"
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: ReturnColumnIds.CONTACT,
        accessorFn: row => row.contact?.name,
        sortId: "contact.nameSort",
        dataType: DataType.CONTACT,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ReturnColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: ReturnColumnIds.FULLY_PAID_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: ReturnColumnIds.STATUS,
        dataType: DataType.BADGE,
        dataAttributes: {
            badgeConfigMap: {
                "draft": {
                    message: {
                        id: "generic-listing.return.badge.draft",
                        defaultMessage: "Draft",
                    },
                    type: "primary",
                },
                "sent": {
                    message: {
                        id: "generic-listing.return.badge.sent",
                        defaultMessage: "Waiting for payment",
                    },
                    type: "info",
                },
                "partially-paid": {
                    message: {
                        id: "generic-listing.return.badge.partially-paid",
                        defaultMessage: "Partially paid",
                    },
                    type: "warning",
                },
                "fully-paid": {
                    message: {
                        id: "generic-listing.return.badge.fully-paid",
                        defaultMessage: "Fully paid",
                    },
                    type: "success",
                },
            },
            textAlign: "right",
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: ReturnColumnIds.TOTAL_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.baseCurrencyTotalAmount",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: ReturnColumnIds.ARCHIVED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: ReturnColumnIds.PO_NUMBER,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: ReturnColumnIds.CUSTOMER_TYPE,
        sortId: "contact.customerTypeName",
        accessorFn: row => row.contact?.customerTypeName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ReturnColumnIds.EFFECTIVE_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: ReturnColumnIds.LAST_PAYMENT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: ReturnColumnIds.BALANCE,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.baseCurrencyBalance",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: ReturnColumnIds.UPDATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: ReturnColumnIds.IMPORTED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: ReturnColumnIds.CONTACT_NAME,
        sortId: "contact.nameSort",
        accessorFn: row => row.contact?.name,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ReturnColumnIds.CONTACT_FIRST_NAME,
        sortId: "contact.firstNameKeyword",
        accessorFn: row => row.contact?.firstName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ReturnColumnIds.CONTACT_LAST_NAME,
        sortId: "contact.lastNameKeyword",
        accessorFn: row => row.contact?.lastName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ReturnColumnIds.CONTACT_ORGANIZATION_NAME,
        sortId: "contact.organizationNameSort",
        accessorFn: row => row.contact?.organizationName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ReturnColumnIds.CONTACT_TYPE,
        accessorFn: row => row.contact?.type,
        dataType: DataType.STRING,
        dataAttributes: {
            messageMap: {
                "persons": {
                    id: "generic-listing.contact-type.people",
                    defaultMessage: "People",
                },
                "organizations": {
                    id: "generic-listing.contact-type.organization",
                    defaultMessage: "Organization",
                },
                "suppliers": {
                    id: "generic-listing.contact-type.supplier",
                    defaultMessage: "Supplier",
                },
            }
        },
        sortable: false,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ReturnColumnIds.CONTACT_PHONE_NUMBER,
        sortId: "contact.phoneNumberKeyword",
        accessorFn: row => row.contact?.phoneNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ReturnColumnIds.CONTACT_TAX_NUMBER,
        sortId: "contact.taxNumber",
        accessorFn: row => row.contact?.taxNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ReturnColumnIds.CONTACT_EMAIL,
        sortId: "contact.emailKeyword",
        accessorFn: row => row.contact?.email,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ReturnColumnIds.PAID_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: ReturnColumnIds.CURRENCY,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: ReturnColumnIds.TAX_ID,
        dataType: DataType.OPTIONS,
        dataAttributes: {
            optionsKey: "taxOptions",
            loadingKey: "loadingTaxOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: ReturnColumnIds.PAYMENT_TRANSACTIONS_TYPE,
        accessorKey: "paymentTransactions",
        sortId: "paymentTransactionsObject.paymentType",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "paymentTypeName",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: ReturnColumnIds.PAYMENT_TRANSACTIONS_AMOUNT,
        accessorKey: "paymentTransactions",
        sortId: "paymentTransactionsObject.amount",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "amount",
            textAlign: "right",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: ReturnColumnIds.PRODUCTS_TITLE,
        accessorKey: "rows",
        sortId: "rowsObject.title",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "title",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: ReturnColumnIds.PRODUCTS_SKU,
        accessorKey: "rows",
        sortId: "rowsObject.sku",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "sku",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: ReturnColumnIds.PRODUCTS_LABELS,
        accessorKey: "rows",
        sortId: "rowsObject.labelIds",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "labelIds",
            optionsKey: "labelOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
]

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.RETURNS,
    columnDefinitions,
    headerMessages: invoiceConfiguration.headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_RETURNS,
    searchPlaceholder: {
        id: "generic-listing.returns.search-placeholder",
        defaultMessage: "Search a return...",
    },
    displayString: {
        id: "generic-listing.returns.display-string",
        defaultMessage: "Returns",
    },
    searchObjectType: ObjectType.INVOICES,
    additionalFilters: {
        billingType: BILLING_TYPE.RETURN,
    },
    bulkActionDefinitions: [],
    exportModalComponent: require("../../../../common/listing/generic-listing/export-modals/InvoiceExportModal"),
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/ReturnCreationButton"),
}

export default configuration



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/return.ts